// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { AlertCircle } from "lucide-react"
import { type DatasourceBill } from '@/types/datasource-bill'

interface BillSummaryCardProps {
  bill: DatasourceBill
}

export function BillSummaryCard({ bill }: BillSummaryCardProps) {
  // Calculate statistics for display
  const versionsWithAmendments = bill.versions.filter(v => v.amendments_detected)
  const versionsWithoutAmendments = bill.versions.filter(v => !v.amendments_detected)
  const processedVersions = bill.versions.filter(v => v.formatting_s3_key)
  const versionsWithText = bill.versions.filter(v => v.text_extracted && v.text_s3_key)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Bill Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{bill.versions.length}</div>
            <div className="text-sm text-muted-foreground">Total Versions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{versionsWithText.length}</div>
            <div className="text-sm text-muted-foreground">With Text</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{processedVersions.length}</div>
            <div className="text-sm text-muted-foreground">With Formatting</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{versionsWithAmendments.length}</div>
            <div className="text-sm text-muted-foreground">With Amendments</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-600">{versionsWithoutAmendments.length}</div>
            <div className="text-sm text-muted-foreground">Clean Versions</div>
          </div>
        </div>
        
        {versionsWithAmendments.length > 0 && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">Amendments detected in this bill</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
